/**
 * カットアウト用テクスチャ（alphaTest で透明部を抜く想定）の RGBA ピクセル列に
 * 対して、透明テクセルの RGB を不透明部分の色で埋める（カラーブリード）。
 *
 * 元画像は透明部分の RGB が白のことが多く（例: 木の葉カードの背景）、mipmap の
 * 縮小平均で白が葉色へ混ざり、遠距離の葉が「白い網状」に崩れて見えてしまう。
 * アルファは変えず色だけ差し替えることで、近距離の見た目（alphaTest の抜き）を
 * 保ったまま遠距離のにじみ色を実色に揃える。
 *
 * 塗り方は 2 段階:
 * 1. 近傍拡張（dilation）: 不透明ピクセルに隣接する透明ピクセルへ色を伝播させる
 *    パスを最大 maxDilationPasses 回繰り返す（輪郭近くは実際の縁色になる）。
 * 2. 残余の平均色フィル: 伝播が届かない奥の透明領域は不透明部分の平均色で埋める
 *    （深い mip レベルでは領域全体が平均化されるため、平均色で十分）。
 */

/** このアルファ値以上を「不透明（色のソース）」として扱う。alphaTest=0.5 相当。 */
export const CUTOUT_ALPHA_THRESHOLD = 128

/** 近傍拡張のパス数の既定値。輪郭から何ピクセルまで実色を伝播させるか。 */
const DEFAULT_DILATION_PASSES = 8

/**
 * RGBA ピクセル列の透明テクセル RGB を不透明部分の色で埋めた新しい配列を返す。
 * 入力 pixels は変更しない。不透明ピクセルが 1 つも無い場合はコピーをそのまま返す。
 */
export const bleedTransparentRgb = (
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  maxDilationPasses: number = DEFAULT_DILATION_PASSES,
): Uint8ClampedArray<ArrayBuffer> => {
  // ImageData コンストラクタが ArrayBuffer 裏付きを要求するため、コピーで確保する。
  const result = new Uint8ClampedArray(pixels)
  const pixelCount = width * height

  // 色が確定済み（不透明 or 伝播済み）のマークと、不透明部分の平均色を作る。
  const filled = new Uint8Array(pixelCount)
  let sumR = 0
  let sumG = 0
  let sumB = 0
  let opaqueCount = 0
  for (let p = 0; p < pixelCount; p += 1) {
    const i = p * 4
    if (result[i + 3] >= CUTOUT_ALPHA_THRESHOLD) {
      filled[p] = 1
      sumR += result[i]
      sumG += result[i + 1]
      sumB += result[i + 2]
      opaqueCount += 1
    }
  }
  if (opaqueCount === 0) {
    return result
  }

  // 1. 近傍拡張: 確定済みピクセルの色を 4 近傍の未確定ピクセルへ伝播させる。
  //    パス内で確定したピクセルを次パスのソースにするため、パスごとに mark を集める。
  //    1024px 級の画像を全パス走査するため、ループ内では配列を生成しない。
  for (let pass = 0; pass < maxDilationPasses; pass += 1) {
    const newlyFilled: number[] = []
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const p = y * width + x
        if (filled[p]) {
          continue
        }
        let sourcePixel = -1
        if (x > 0 && filled[p - 1]) {
          sourcePixel = p - 1
        } else if (x < width - 1 && filled[p + 1]) {
          sourcePixel = p + 1
        } else if (y > 0 && filled[p - width]) {
          sourcePixel = p - width
        } else if (y < height - 1 && filled[p + width]) {
          sourcePixel = p + width
        }
        if (sourcePixel < 0) {
          continue
        }
        const i = p * 4
        const ni = sourcePixel * 4
        result[i] = result[ni]
        result[i + 1] = result[ni + 1]
        result[i + 2] = result[ni + 2]
        newlyFilled.push(p)
      }
    }
    if (newlyFilled.length === 0) {
      break
    }
    for (const p of newlyFilled) {
      filled[p] = 1
    }
  }

  // 2. 残余の平均色フィル: 伝播が届かなかった透明領域を平均色で埋める。
  const avgR = Math.round(sumR / opaqueCount)
  const avgG = Math.round(sumG / opaqueCount)
  const avgB = Math.round(sumB / opaqueCount)
  for (let p = 0; p < pixelCount; p += 1) {
    if (!filled[p]) {
      const i = p * 4
      result[i] = avgR
      result[i + 1] = avgG
      result[i + 2] = avgB
    }
  }
  return result
}
